import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BehaviorSubject,
  Subject,
  Subscription,
  combineLatest,
  distinctUntilChanged,
  map,
  shareReplay,
  takeUntil,
} from 'rxjs';
import { Users } from 'server/shared/collections/users';
import { Certificate } from 'server/shared/models/certificate';
import { RoleEnum } from 'server/shared/models/role.enum';
import { DispoUser, MappedCertificate } from 'server/shared/models/user';
import { rxNgZoneRun } from '../../../helpers/rxNgZoneRun';

import { CertificateGroup } from 'server/shared/models/certificateGroup';
import { CertificateService } from 'src/app/services/certificate.service';
import { CustomerService } from 'src/app/services/customer.service';
@Component({
  selector: 'app-certificate-select',
  templateUrl: './certificate-select.component.html',
  styleUrls: ['./certificate-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CertificateSelectComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  certificateInputSubscription?: Subscription;
  certificatesSubscription?: Subscription;
  destroyed$ = new Subject<void>();
  allCertificates$ = new BehaviorSubject<Certificate[]>([]);
  searchTerm$ = new BehaviorSubject<string | undefined>(undefined);
  filteredCertificates$ = new BehaviorSubject<Certificate[]>([]);
  recentlyUsedCertificateIds$ = new BehaviorSubject<string[]>([]);

  getParameterSeparator = ',';

  @Output() selectionChanged = new EventEmitter<Certificate[]>();

  disabled$ = new BehaviorSubject<boolean>(false);
  @Input() set disabled(value: boolean) {
    this.disabled$.next(value);
  }
  get disabled() {
    return this.disabled$.value;
  }

  showHint$ = new BehaviorSubject<boolean>(false);
  @Input() set showHint(value: boolean) {
    this.showHint$.next(value);
  }
  get showHint() {
    return this.showHint$.value;
  }

  addOnly$ = new BehaviorSubject<boolean>(false);
  @Input() set addOnly(value: boolean) {
    this.addOnly$.next(value);
  }
  get addOnly() {
    return this.addOnly$.value;
  }

  required$ = new BehaviorSubject<boolean>(false);
  @Input() set required(value: boolean) {
    this.required$.next(value);
  }
  get required() {
    return this.required$.value;
  }

  lightFormField$ = new BehaviorSubject<boolean>(false);
  @Input() set lightFormField(value: boolean) {
    this.lightFormField$.next(value);
  }
  get lightFormField() {
    return this.lightFormField$.value;
  }

  doNotUpdateRecentlyUsed$ = new BehaviorSubject<boolean>(false);
  @Input() set doNotUpdateRecentlyUsed(value: boolean) {
    this.doNotUpdateRecentlyUsed$.next(value);
  }
  get doNotUpdateRecentlyUsed() {
    return this.doNotUpdateRecentlyUsed$.value;
  }

  withIcon$ = new BehaviorSubject<boolean>(false);
  @Input() set withIcon(value: boolean) {
    this.withIcon$.next(value);
  }
  get withIcon() {
    return this.withIcon$.value;
  }

  noCertificateGroups$ = new BehaviorSubject<boolean>(false);
  @Input() set noCertificateGroups(value: boolean) {
    this.noCertificateGroups$.next(value);
  }
  get noCertificateGroups() {
    return this.noCertificateGroups$.value;
  }

  selectedCertificates$ = new BehaviorSubject<Certificate[]>([]);
  selectedCertificateIds$ = new BehaviorSubject<string[]>([]);

  @Input() set selectedCertificates(certificates: Certificate[]) {
    this.setSelectedCertificates(certificates);
  }
  get selectedCertificates() {
    return this.selectedCertificates$.value;
  }

  @Input() set selectedMappedCertificates(
    mappedCertificates: MappedCertificate[],
  ) {
    if (mappedCertificates?.length) {
      const selectedIds = mappedCertificates.map(
        (jobCertificate) => jobCertificate.id,
      );
      this.selectedCertificateIds$.next(selectedIds);
      this.setSelectedCertificates(
        mappedCertificates.map((certificate) => {
          return {
            _id: certificate.id,
            name: certificate.displayName,
          };
        }),
      );

      this.setSelectedCertificateIds(selectedIds);
    } else {
      this.setSelectedCertificates([]);
    }
  }
  currentUserTracker?: Tracker.Computation;
  certificatesTracker?: Tracker.Computation;

  _excludedCertificates$ = new BehaviorSubject<Certificate[]>([]);
  _excludedCertificateIds$ = new BehaviorSubject<string[]>([]);

  @Input() set excludedCertificates(certificates: Certificate[]) {
    this.setExcludedCertificate(certificates);
  }

  @Input() set excludedMappedCertificates(
    mappedCertificates: MappedCertificate[],
  ) {
    if (mappedCertificates?.length) {
      const excludedIds = mappedCertificates.map(
        (jobCertificate) => jobCertificate.id,
      );
      this._excludedCertificateIds$.next(excludedIds);
      this.setExcludedCertificateIds(excludedIds);
    } else {
      this.setExcludedCertificate([]);
    }
  }

  @ViewChild('certificateInput', { static: false })
  certificateInput?: NgControl;
  @Input() useQueryParam: string | false = false;

  customerCertificateGroups$ = combineLatest([
    this.customerService.readyDispoCustomer$,
    this.selectedCertificates$,
  ]).pipe(
    map(([customer, certificates]) => {
      let certificateGroups = customer?.certificateGroups || [];

      certificateGroups = certificateGroups.filter((certificateGroup) => {
        return !certificateGroup.certificates.every((certificate) =>
          certificates.some((c) => c._id === certificate.id),
        );
      });

      return certificateGroups;
    }),
    shareReplay(1),
    takeUntil(this.destroyed$),
  );

  constructor(
    private ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private certificateService: CertificateService,
    private customerService: CustomerService,
  ) {
    this.route.queryParams
      .pipe(
        map((params) => params[this.useQueryParam || ''] ?? undefined),
        distinctUntilChanged(),
        rxNgZoneRun(this.ngZone),
      )
      .subscribe((certificateIdString) => {
        if (this.useQueryParam && !certificateIdString) {
          this.setSelectedCertificateIds([]);
        }
      });
  }

  ngOnInit(): void {
    let init = true;
    this.certificateService.readyCertificates$
      .pipe(rxNgZoneRun(this.ngZone), takeUntil(this.destroyed$))
      .subscribe((certificates) => {
        this.allCertificates$.next(certificates);
        this.filterCertificates();
        if (this.selectedCertificateIds$.value.length) {
          this.setSelectedCertificateIds(this.selectedCertificateIds$.value);
        }
        if (this._excludedCertificateIds$.value.length) {
          this.setExcludedCertificateIds(this._excludedCertificateIds$.value);
        }
        if (init) {
          this.getInitialQueryParams();
          init = false;
        }
      });

    this.currentUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        // Get the currently logged in user to access recently used certificates
        const currentUser = Users.findOne(Meteor.userId()) as DispoUser;
        if (
          currentUser &&
          currentUser.role === RoleEnum.DISPO &&
          currentUser.recentlyUsed?.certificates
        ) {
          this.recentlyUsedCertificateIds$.next(
            currentUser.recentlyUsed.certificates,
          );
          this.filterCertificates();
        }
      });
    });

    if (this.useQueryParam) {
      this.route.queryParams
        .pipe(
          map((params) => params[this.useQueryParam || ''] ?? undefined),
          distinctUntilChanged(),
          rxNgZoneRun(this.ngZone),
        )
        .subscribe((certificateIdString) => {
          if (certificateIdString) {
            const certificateIds = certificateIdString.split(
              this.getParameterSeparator,
            );
            this.selectedCertificateIds$.next(certificateIds);
            this.selectedCertificates$.next(
              this.allCertificates$.value.filter((certificate) =>
                certificateIds.includes(certificate._id),
              ),
            );
            this.emitValue(false);
          }
        });
    }
  }

  ngAfterViewInit(): void {
    this.certificateInputSubscription =
      this.certificateInput?.valueChanges?.subscribe((value) => {
        if (typeof value == 'string') {
          this.searchTerm$.next(value);
        } else {
          this.searchTerm$.next(undefined);
        }
        this.filterCertificates();
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.certificatesSubscription) {
      this.certificatesSubscription.unsubscribe();
    }
    if (this.certificateInputSubscription) {
      this.certificateInputSubscription.unsubscribe();
    }
    if (this.currentUserTracker) {
      this.currentUserTracker.stop();
    }
    if (this.certificatesTracker) {
      this.certificatesTracker.stop();
    }
  }

  emitValue(updateQueryParams = true) {
    if (this.useQueryParam && updateQueryParams) {
      this.updateQueryParams({
        [this.useQueryParam]:
          this.selectedCertificates
            .map((certificate) => certificate._id)
            .join(this.getParameterSeparator) || '',
      });
    }
    this.selectionChanged.emit(this.selectedCertificates);
  }

  getInitialQueryParams(): void {
    let queryParams = this.route.snapshot.queryParams;
    if (this.useQueryParam && queryParams[this.useQueryParam] !== undefined) {
      const certificateIds = queryParams[this.useQueryParam].split(
        this.getParameterSeparator,
      );
      this.selectedCertificates$.next(
        this.allCertificates$.value.filter((certificate) =>
          certificateIds.includes(certificate._id),
        ),
      );
      this.emitValue(false);
    }
  }

  selectedCertificate(matSelectedEvent: MatAutocompleteSelectedEvent) {
    if (matSelectedEvent.option.value?.certificates) {
      this.selectCertificateGroup(matSelectedEvent.option.value);
      return;
    }
    const certificate = matSelectedEvent.option.value;
    const currentCerts = [...this.selectedCertificates];
    currentCerts.push(certificate);
    this.selectedCertificates$.next(currentCerts);

    if (!this.doNotUpdateRecentlyUsed) {
      Meteor.call('addRecentlyUsedCertificate', certificate._id);
    }

    this.updateSelectElement();
  }

  private filterCertificates() {
    let filteredCertificates = this.allCertificates$.value;
    const searchTerm = this.searchTerm$.value;
    if (searchTerm) {
      filteredCertificates = filteredCertificates.filter((certificate) =>
        certificate.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (this._excludedCertificates$.value.length) {
      filteredCertificates = filteredCertificates.filter(
        (certificate) =>
          !this._excludedCertificates$.value.some(
            (excludedCertificate) =>
              excludedCertificate._id === certificate._id,
          ),
      );
    }

    // Sort by recently used first, then alphabetically
    filteredCertificates = filteredCertificates.sort((a, b) => {
      const aIndex = this.recentlyUsedCertificateIds$.value.indexOf(a._id);
      const bIndex = this.recentlyUsedCertificateIds$.value.indexOf(b._id);

      // If both are in recently used list
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex; // Sort by position in recently used
      }
      // If only a is in recently used
      else if (aIndex !== -1) {
        return -1; // a comes first
      }
      // If only b is in recently used
      else if (bIndex !== -1) {
        return 1; // b comes first
      }
      // Neither are recently used, sort alphabetically
      else {
        return a.name.localeCompare(b.name);
      }
    });
    this.filteredCertificates$.next(filteredCertificates);
  }

  removeCertificate(certificateId: string) {
    if (this.addOnly) return;
    this.selectedCertificates$.next(
      this.selectedCertificates.filter(
        (certificate) => certificate._id !== certificateId,
      ),
    );
    this.emitValue();
  }

  setSelectedCertificates(certificates: Certificate[]) {
    this.ngZone.run(() => {
      this.selectedCertificates$.next(certificates);
    });
  }

  setSelectedCertificateIds(certificateIds: string[]) {
    this.selectedCertificates$.next(
      this.allCertificates$.value.filter((certificate) =>
        certificateIds.includes(certificate._id),
      ),
    );
  }

  setExcludedCertificate(certificates: Certificate[]) {
    this._excludedCertificates$.next(certificates);
    this.filterCertificates();
  }

  setExcludedCertificateIds(certificateIds: string[]) {
    this._excludedCertificates$.next(
      this.allCertificates$.value.filter((certificate) =>
        certificateIds.includes(certificate._id),
      ),
    );
    this.filterCertificates();
  }

  private updateQueryParams(queryParams: { [key: string]: string }) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })
        .then();
    });
  }

  selectCertificateGroup(certificateGroup: CertificateGroup) {
    let certificates = this.selectedCertificates$.value;

    certificates.push(
      ...certificateGroup.certificates.map((certificate) => {
        return { _id: certificate.id, name: certificate.displayName };
      }),
    );

    // remove duplicates
    certificates = certificates.filter(
      (cert, index, array) =>
        array.findIndex((c) => c._id === cert._id) === index,
    );

    this.selectedCertificateIds$.next(certificates.map((cert) => cert._id));
    this.selectedCertificates$.next(certificates);
    this.updateSelectElement();
  }

  updateSelectElement() {
    this.emitValue();
    // @ts-ignore
    this.certificateInput.control.nativeElement.blur();
    // @ts-ignore
    this.certificateInput.control.nativeElement.value = '';
    this.certificateInput?.reset('');
    this.certificateInput?.control?.markAsDirty();
  }

  trackByCertificateId(index: number, certificate: Certificate): string {
    return certificate._id;
  }

  trackByCertificateGroupId(
    index: number,
    certificateGroup: CertificateGroup,
  ): string {
    return certificateGroup.id;
  }
}
